import { Link } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useCouponsList } from '../hooks';
import { useCouponFilters } from '../store/filters.store';
import { CouponStatusBadge } from './CouponStatusBadge';
import { TableSkeleton } from '@/src/modules/components/ui/LoadingSkeleton';
import { EmptyState, ErrorState } from '@/src/modules/components/ui/EmptyState';
import { describeDiscount } from '../lib/discount';

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const th = 'px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-on-surface-variant';

export function CouponTable() {
  const filters = useCouponFilters(useShallow((s) => ({ search: s.search, status: s.status })));
  const { data, isLoading, isError, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useCouponsList(filters);

  if (isLoading) return <TableSkeleton cols={6} />;
  if (isError) return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;

  const rows = data?.pages.flatMap((p) => p.items) ?? [];
  if (rows.length === 0) {
    return filters.search || filters.status ? (
      <EmptyState title="No coupons match these filters" hint="Try clearing filters or widening the search." />
    ) : (
      <EmptyState title="No coupons yet" hint="Create one — it starts as a Draft until you activate it." />
    );
  }

  return (
    <div className="glass-panel rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-surface-container-low">
            <tr>
              <th className={th}>Code</th>
              <th className={th}>Discount</th>
              <th className={th}>Validity</th>
              <th className={th}>Usage</th>
              <th className={th}>Status</th>
              <th className={th}>Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-border-subtle hover:bg-white/5">
                <td className="px-3 py-2">
                  <Link to={`/coupons/${c.id}`} className="font-mono text-sm font-bold hover:text-electric-violet">
                    {c.code}
                  </Link>
                  <div className="text-[10px] text-on-surface-variant truncate max-w-[240px]">{c.label}</div>
                </td>
                <td className="px-3 py-2 text-sm">{describeDiscount(c)}</td>
                <td className="px-3 py-2 text-xs text-on-surface-variant whitespace-nowrap">
                  {fmtDate(c.startAt)} → {fmtDate(c.endAt)}
                </td>
                <td className="px-3 py-2 text-sm">
                  {c.usedCount} / {c.usageLimitGlobal ?? '∞'}
                </td>
                <td className="px-3 py-2">
                  <CouponStatusBadge coupon={c} />
                </td>
                <td className="px-3 py-2 text-xs text-on-surface-variant">{fmtDate(c.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasNextPage && (
        <div className="flex justify-center px-4 py-3 border-t border-border-subtle">
          <button
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="px-3 py-1.5 rounded-md border border-border-subtle text-xs disabled:opacity-40 hover:bg-white/5"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
